import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import styled from 'foundations-theming/utils';
import { fwcBorderRadius } from 'foundations-theming/utils/fwc';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { StyledButton, UnstyledButton } from '../Button/PrivateButton';
import { useIsKeyboardMode } from '../../../components/AppProvider/KeyboardModeProvider';
import { StyledButtonLink, UnstyledButtonLink } from '../Button/PrivateButtonLink';
import { MERCURY_LAYER, VENUS_LAYER, DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE } from '../../constants';
import { useFloatingButtonGroupContext } from '../FloatingButtonGroup/FloatingButtonGroupContext';
import { createHoverStateSelector, getScreenReaderOnlyStyles } from '../../utils/styles/styles';
import { jsx as _jsx } from "react/jsx-runtime";
const createFocusGuardButtonSelector = () => {
  const buttonComponents = [StyledButton, UnstyledButton, StyledButtonLink, UnstyledButtonLink];
  return buttonComponents.flatMap(component => [`& [data-floating-ui-focus-guard] + ${component}`, `& [data-floating-ui-focus-guard] ~ [data-floating-ui-focus-guard] + ${component}`]).join(', ');
};
export const StyledButtonGroup = styled.div.withConfig({
  displayName: "ButtonGroup__StyledButtonGroup"
})(({
  $floatingGroup,
  $orientation,
  $srOnly,
  $use,
  $cssStyles,
  theme
}) => {
  const isKeyboardMode = useIsKeyboardMode();
  // when the parent is a FloatingButtonGroup, $floatingGroup is true and $use is 'default'
  const floating = $use === 'floating' || $floatingGroup;
  return Object.assign({
    display: $orientation === 'horizontal' ? 'inline-block' : 'inline-flex',
    flexDirection: $orientation === 'vertical' ? 'column' : undefined
  }, floating ? Object.assign({
    marginBlockStart: `-${theme.space['400']}`,
    paddingBlockStart: theme.space['400'],
    position: 'absolute',
    zIndex: MERCURY_LAYER
  }, $use === 'floating' ? {
    insetBlockStart: theme.space['0'],
    insetInlineEnd: theme.space['0'],
    insetInlineStart: theme.space['0']
  } : {
    insetBlockStart: `-${theme.space['600']}`,
    insetInlineEnd: theme.space['250']
  }) : undefined, $cssStyles, {
    // Targeting links (a) allows for proper styling of UIRouterIconButton and UIRouterNavButton
    [`& ${StyledButton}, & ${UnstyledButton}, & ${StyledButtonLink}, & ${UnstyledButtonLink}, & > a`]: Object.assign({
      marginBlockStart: $orientation === 'horizontal' ? theme.space['0'] : undefined,
      marginInlineStart: $orientation === 'vertical' ? theme.space['0'] : undefined,
      position: 'relative',
      display: $orientation === 'vertical' ? 'block' : undefined
    }, floating ? {
      fontSize: theme.typography['detail-100'].fontSize,
      lineHeight: theme.typography['detail-100'].fontSize,
      // Using font size here since the font size and line height are the same
      maxHeight: '32px',
      paddingBlock: '9px',
      backgroundColor: theme.color['fill-transitional-tertiary-default'],
      borderBlockColor: `${theme.color['border-transitional-tertiary-default']} !important`,
      borderInlineColor: `${theme.color['border-transitional-tertiary-default']} !important`,
      color: theme.color['text-inverse-default'],
      '& svg[data-icon-name]': {
        fill: theme.color['text-inverse-default']
      },
      "&:not([aria-disabled='true'])": {
        [createHoverStateSelector('&:hover')]: {
          backgroundColor: theme.color['fill-transitional-tertiary-hover'],
          color: theme.color['text-inverse-default'],
          borderBlockColor: `${theme.color['border-transitional-tertiary-default']} !important`,
          borderInlineColor: `${theme.color['border-transitional-tertiary-default']} !important`,
          '& svg[data-icon-name]': {
            fill: theme.color['text-inverse-default']
          }
        },
        "&:active, &[aria-pressed='true']": {
          backgroundColor: theme.color['fill-transitional-tertiary-pressed'],
          color: theme.color['text-inverse-default'],
          borderBlockColor: `${theme.color['border-transitional-tertiary-default']} !important`,
          borderInlineColor: `${theme.color['border-transitional-tertiary-default']} !important`
        }
      }
    } : undefined, {
      /**
       * Border radius styles
       *
       * Outer corners are explicitly set to the fwc-button token so they
       * match the Button's natural radius even when a child (e.g. a
       * Dropdown trigger) overrides borderRadius via style props.
       */
      '&:not(:only-child)': {
        '&:first-child': {
          borderStartStartRadius: theme.borderRadius[fwcBorderRadius('fwc-button')],
          borderStartEndRadius: $orientation === 'horizontal' ? theme.borderRadius['0'] : theme.borderRadius[fwcBorderRadius('fwc-button')],
          borderEndStartRadius: $orientation === 'vertical' ? theme.borderRadius['0'] : theme.borderRadius[fwcBorderRadius('fwc-button')],
          borderEndEndRadius: theme.borderRadius['0']
        },
        /**
         * Selects the last <a> or <button> element among its siblings, ignoring any other element types.
         * nth-last-child counts from the end (last child to the first child) and -n + 1 always computes to the last child since 0th and negative numbered elements are not valid.
         * This is a bit of a hack to get the border radius styles to work with Dropdown, which uses floating-ui that adds span focus guard elements after the button anchor element.
         * nth-last-child(... of ...) is not supported by ALL browsers, but it is supported by all major browsers.
         */
        '&:nth-last-child(-n + 1 of a, button)': {
          borderStartStartRadius: theme.borderRadius['0'],
          borderStartEndRadius: $orientation === 'vertical' ? theme.borderRadius['0'] : theme.borderRadius[fwcBorderRadius('fwc-button')],
          borderEndStartRadius: $orientation === 'horizontal' ? theme.borderRadius['0'] : theme.borderRadius[fwcBorderRadius('fwc-button')],
          borderEndEndRadius: theme.borderRadius[fwcBorderRadius('fwc-button')]
        },
        '&:not(&:first-child):not(:nth-last-child(-n + 1 of a, button))': {
          borderStartStartRadius: theme.borderRadius['0'],
          borderStartEndRadius: theme.borderRadius['0'],
          borderEndEndRadius: theme.borderRadius['0'],
          borderEndStartRadius: theme.borderRadius['0']
        }
      },
      '&:not(&:first-child)': {
        marginInlineStart: $orientation === 'horizontal' ? '-1px' : undefined,
        // Doesn't need to be tokenized
        marginBlockStart: $orientation === 'vertical' ? '-1px' : undefined // Doesn't need to be tokenized
      },
      '&:focus': {
        zIndex: isKeyboardMode ? VENUS_LAYER : undefined
      },
      "&[aria-pressed='true']:not([aria-disabled='true'])": {
        zIndex: MERCURY_LAYER
      },
      "&[aria-disabled='true']": {
        color: theme.color['icon-core-disabled'],
        backgroundColor: theme.color['fill-tertiary-default'],
        borderColor: `${theme.color['border-core-default']} !important`
      }
    })
  }, $srOnly && Object.assign({}, getScreenReaderOnlyStyles(theme)), {
    /**
     * When a Dropdown is open, floating-ui injects focus guard spans which can cause
     * the Button's autospacing styles to apply marginInlineStart via adjacent sibling
     * selectors. This override ensures those margins are reset in ButtonGroup context.
     */
    [createFocusGuardButtonSelector()]: {
      marginInlineStart: $orientation === 'vertical' ? theme.space['0'] : '-1px',
      marginBlockStart: $orientation === 'horizontal' ? theme.space['0'] : '-1px'
    }
  });
});
export const ButtonGroup = _ref => {
  let {
      children,
      use = 'default',
      orientation = 'horizontal',
      role = 'group',
      onClick,
      [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: dataComponentAttribute
    } = _ref,
    restProps = _objectWithoutPropertiesLoose(_ref, ["children", "use", "orientation", "role", "onClick", DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE].map(_toPropertyKey));
  const {
    domProps,
    tokenProps
  } = filterProps(restProps);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const floatingButtonGroupContext = useFloatingButtonGroupContext();
  const className = floatingButtonGroupContext.srOnly ? 'sr-only' : undefined;
  return /*#__PURE__*/_jsx(StyledButtonGroup, Object.assign({}, domProps, {
    className: className,
    role: role,
    onClick: onClick,
    $floatingGroup: floatingButtonGroupContext.floatingGroup,
    $srOnly: floatingButtonGroupContext.srOnly,
    $orientation: orientation,
    $use: use,
    $cssStyles: cssStyles,
    [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: dataComponentAttribute || 'ButtonGroup',
    children: children
  }));
};
ButtonGroup.displayName = 'ButtonGroup';